/**
 * Main-agent pending ask QUEUE (incident a671cd77).
 *
 * A native turn can mint several main-agent interactions in the same instant
 * (parallel tool batch — the meta-executor gates inner tools individually).
 * `TaskState.pendingInteraction` stays the visible HEAD so every reader
 * (InputStation, attention chrome, selectors) keeps working unchanged;
 * `TaskState.pendingQueue` holds the FIFO tail. Settling the head promotes
 * the next sibling, walking the user through every ask instead of silently
 * dropping all but the last one.
 *
 * Invariant: a non-empty `pendingQueue` implies a non-null `pendingInteraction`.
 * ALL main-lane mutations must go through these helpers to preserve it.
 * (Subagent pauses live in `pendingByLane` and never enter this queue.)
 */

import type { PendingInteraction, TaskState } from "./types";

/** The main-agent lane: visible head + FIFO tail. */
type MainLane = Pick<
  TaskState,
  "pendingInteraction" | "pendingQueue" | "resumingFrom"
>;

/** Identity key for matching: Input Station interaction id, legacy requestId. */
export function pendingKey(pi: PendingInteraction): string {
  return pi.interactionId ?? pi.requestId;
}

/**
 * Hold a new main-agent pending. Becomes the head if the slot is free; a
 * duplicate key (reconnect / hydration re-emit) replaces its entry in place;
 * otherwise it joins the FIFO tail.
 */
export function enqueueMainPending(
  task: MainLane,
  pi: PendingInteraction
): void {
  task.pendingQueue ??= [];
  const key = pendingKey(pi);
  if (task.pendingInteraction && pendingKey(task.pendingInteraction) === key) {
    task.pendingInteraction = pi;
    return;
  }
  const idx = task.pendingQueue.findIndex((q) => pendingKey(q) === key);
  if (idx !== -1) {
    task.pendingQueue[idx] = pi;
    return;
  }
  if (task.pendingInteraction) task.pendingQueue.push(pi);
  else task.pendingInteraction = pi;
}

/**
 * Settle (remove) one main-agent pending. No key ⇒ the head. Settling the
 * head promotes the next queued sibling; settling a queued sibling leaves the
 * head alone. Returns the settled interaction, or null if nothing matched.
 */
export function settleMainPending(
  task: MainLane,
  key?: string
): PendingInteraction | null {
  task.pendingQueue ??= [];
  const head = task.pendingInteraction;
  if (head && (key === undefined || pendingKey(head) === key)) {
    task.pendingInteraction = task.pendingQueue.shift() ?? null;
    // The ask settled; until the turn reopens the task is resuming FROM it
    // (the line says so — never "done"). AGENT_START clears it.
    if (task.pendingInteraction === null) task.resumingFrom = head.type;
    return head;
  }
  if (key === undefined) return null;
  const idx = task.pendingQueue.findIndex((q) => pendingKey(q) === key);
  if (idx === -1) return null;
  return task.pendingQueue.splice(idx, 1)[0] ?? null;
}

/**
 * Put a failed-to-resolve interaction back at the FRONT — the user saw it
 * first and must be able to retry; a promoted sibling steps back behind it.
 * No-op when the interaction is already held (never duplicates).
 */
export function restoreMainPending(
  task: MainLane,
  pi: PendingInteraction
): void {
  task.pendingQueue ??= [];
  if (findMainPending(task, pendingKey(pi))) return;
  if (task.pendingInteraction)
    task.pendingQueue.unshift(task.pendingInteraction);
  task.pendingInteraction = pi;
}

/** Look up a held main-agent pending (head or queued) by key. */
export function findMainPending(
  task: MainLane,
  key: string
): PendingInteraction | null {
  if (task.pendingInteraction && pendingKey(task.pendingInteraction) === key) {
    return task.pendingInteraction;
  }
  return task.pendingQueue?.find((q) => pendingKey(q) === key) ?? null;
}

/** Drop every held main-agent pending matching `pred`, promoting as needed. */
export function removeMainPendingWhere(
  task: MainLane,
  pred: (pi: PendingInteraction) => boolean
): void {
  task.pendingQueue = (task.pendingQueue ?? []).filter((q) => !pred(q));
  if (task.pendingInteraction && pred(task.pendingInteraction)) {
    task.pendingInteraction = task.pendingQueue.shift() ?? null;
  }
}
